import {fetchSpendingByAccountIfNeeded, invalidateSpendingByAccount} from 'modules/store/spendingByAccount';
import {fetchSpendingByCostCenterIfNeeded, invalidateSpendingByCostCenter} from 'modules/store/spendingByCostCenter';
import {fetchSpendingByProductAreaIfNeeded, invalidateSpendingByProductArea} from 'modules/store/spendingByProductArea';
import {fetchSpendingByVendorIfNeeded, invalidateSpendingByVendor, getEntitiesSpendingByVendor} from 'modules/store/spendingByVendor';
import {fetchTopByProductAreaIfNeeded, invalidateTopByProductArea} from 'modules/store/topByProductArea';
import {fetchTopByCostCenterIfNeeded, invalidateTopByCostCenter} from 'modules/store/topByCostCenter';
import {fetchSpendingLastYearIfNeeded, invalidateSpendingLastYear, getEntitiesSpendingLastYear} from 'modules/store/spendingLastYear';
import {fetchTopByAccountIfNeeded, invalidateTopByAccount} from 'modules/store/topByAccount';
import {invalidateLastRun} from 'modules/consolidateLastRun';
import {invalidateTotalSpending} from 'modules/store/totalSpending';
import {CHANGE_PERIOD} from 'modules/period';

import {getChartMonth, getChartTop, getColors} from 'util/charts';
import {INIT_INDEX, ONE_ELEMENT, SORT, DEFAULT_VALUE, DEFAULT_ARRAY} from 'util/constants';
import {createData} from 'util/dashboard';

const NEXT_MONTH = 1;
const NEXT2_MONTH = 2;
const PLOT_ADJUST = 0.5;

const defaultSelectedSeries = [];

export const getCurrentPeriod = () => {
    const CURRENT_MONTH = 1;
    const PRED_INDEX = 0;
    const date = new Date();
    const firstDay = new Date(date.getFullYear(), date.getMonth(), CURRENT_MONTH).getTime();
    const lastDay = new Date(date.getFullYear(), date.getMonth() + CURRENT_MONTH, PRED_INDEX).getTime();

    return {
        startDate: firstDay,
        endDate:   lastDay
    };
};

const _getDefaultState = () => ({
    spendingLastYearConfig: null,
    isFetching:             {},
    todayPeriod:            module.exports.getCurrentPeriod(),
    breakdown:              {
        spendingConcept: 'account',
        showDetails:     false,
        selectedSeries:  defaultSelectedSeries,
        chart:           {
            account:     getChartMonth(),
            vendor:      getChartMonth(),
            productArea: getChartMonth(),
            costCenter:  getChartMonth()
        }
    },
    topByConcept: {
        productArea: {
            data: []
        },
        costCenter: {
            data: []
        },
        account: {
            data: []
        }
    },
    forecast: []
});

export const STORE_SPENDING_VENDOR = 'spendingByVendor';
export const STORE_TOP_PRODUCTAREA = 'topByProductArea';
export const STORE_TOP_COSTCENTER = 'topByCostCenter';
export const STORE_TOP_ACCOUNT = 'topByAccount';
export const STORE_SPENDING_LAST = 'spendingLastYear';

export const fetchDashboard = () => (dispatch, getState) => {
    const state = getState();
    const selectedConcept = state.dashboard.breakdown.spendingConcept;
    const period = state.period.dashboard;
    const {todayPeriod} = state.dashboard;

    const stores = [
        STORE_SPENDING_VENDOR,
        STORE_TOP_PRODUCTAREA,
        STORE_TOP_COSTCENTER,
        STORE_TOP_ACCOUNT,
        STORE_SPENDING_LAST
    ];

    dispatch(setFetchingDashboard(stores, stores.map(() => true)));

    let promise;

    switch (selectedConcept) {
    case 'account':
        promise = dispatch(fetchSpendingByAccountIfNeeded(period));
        break;
    case 'costCenter':
        promise = dispatch(fetchSpendingByCostCenterIfNeeded(period));
        break;
    case 'productArea':
        promise = dispatch(fetchSpendingByProductAreaIfNeeded(period));
        break;
    case 'vendor':
        promise = dispatch(fetchSpendingByVendorIfNeeded(period));
        break;
    default:
        break;
    }

    return promise
        .then(() => dispatch(fetchTopByCostCenterIfNeeded(period)))
        .then(() => { dispatch(setFetchingDashboard(STORE_TOP_COSTCENTER, false)) })
        .then(() => dispatch(fetchTopByProductAreaIfNeeded(period)))
        .then(() => { dispatch(setFetchingDashboard(STORE_TOP_PRODUCTAREA, false)) })
        .then(() => dispatch(fetchTopByAccountIfNeeded(period)))
        .then(() => { dispatch(setFetchingDashboard(STORE_TOP_ACCOUNT, false)) })
        .then(() => dispatch(fetchSpendingLastYearIfNeeded()))
        .then(() => dispatch(fetchSpendingByVendorIfNeeded(todayPeriod)))
        .then(() => { dispatch(setFetchingDashboard(STORE_SPENDING_VENDOR, false)) })
        .then(() => dispatch(generateForecast()))
        .then(() => { dispatch(setFetchingDashboard(STORE_SPENDING_LAST, false)) });
};

export const invalidateDashboard = () => dispatch => {
    dispatch(invalidateSpendingByAccount());
    dispatch(invalidateSpendingByCostCenter());
    dispatch(invalidateSpendingByProductArea());
    dispatch(invalidateSpendingByVendor());
    dispatch(invalidateSpendingLastYear());
    dispatch(invalidateTopByAccount());
    dispatch(invalidateTopByCostCenter());
    dispatch(invalidateTopByProductArea());
    dispatch(invalidateTotalSpending());
    dispatch(invalidateLastRun());
};

const GENERATE_FORECAST = 'GENERATE_FORECAST';

export const generateForecast = () => (dispatch, getState) => {
    const state = getState();
    const spendingLastYear = getEntitiesSpendingLastYear(state);
    const spending = getEntitiesSpendingByVendor(state, state.dashboard.todayPeriod);
    const spendingData = spending.data.length ? spending.data[INIT_INDEX] : {results: DEFAULT_ARRAY,totals: {}};

    return dispatch({
        type:             GENERATE_FORECAST,
        last12Months:     spendingLastYear.data,
        spendingByVendor: spendingData.results
    });
};
const _generateForecast = (state, {last12Months, spendingByVendor}) => ({
    ...state,
    forecast: createData(last12Months, spendingByVendor)
});

const SELECT_SPENDING_CONCEPT = 'SELECT_SPENDING_CONCEPT';

export const selectSpendingConcept = concept => ({
    type: SELECT_SPENDING_CONCEPT,
    concept
});

const FILL_SELECTED_TOTAL_SPENDING_SERIES = 'FILL_SELECTED_TOTAL_SPENDING_SERIES';

export const fillSelectedSpendingSeries = series => ({
    type:           FILL_SELECTED_TOTAL_SPENDING_SERIES,
    selectedSeries: series
});

const _fillSelectedSpendingSeries = series => {
    series.fillCalled = true;

    return series;
};

const TOGGLE_SELECTED_SERIES = 'TOGGLE_SELECTED_SERIES';

export const toggleSelectedSpendingSeries = selectedSerie => ({
    type:  TOGGLE_SELECTED_SERIES,
    serie: selectedSerie
});

const SET_FETCHING_DASHBOARD = 'SET_FETCHING_DASHBOARD';

export const setFetchingDashboard = (store, value) => ({
    type: SET_FETCHING_DASHBOARD,
    store, value
});
export const _setFetchingDashboard = (state, action) => {
    const _do = (store, value) => {
        if (store instanceof Array) {
            return store.reduce((ret, item, index) => ({
                ...ret,
                ..._do(item, value[index])
            }), {});
        }

        return {
            [store]: value
        };
    };

    return {
        ...state,
        isFetching: {
            ...state.isFetching,
            ..._do(action.store, action.value)
        }
    };
};

const CREATE_BREAKDOWN_CHART_CONFIG = 'CREATE_BREAKDOWN_CHART_CONFIG';

export const createBreakdownChartConfig = (data, colors, handleSelected, handleLoad, handleClick) => ({
    type: CREATE_BREAKDOWN_CHART_CONFIG,
    data, colors, handleSelected, handleLoad, handleClick
});

const _createBreakdownChartConfig = (series, colors, handleSelected, handleLoad, handleClick) => ({
    ...getChartMonth(handleSelected, handleLoad, handleClick),
    colors: colors || getColors(series.length),
    series: series.map(serie => ({
        type: 'areaspline',
        id:   serie.entity.code,
        name: serie.entity.title,
        data: serie.data.map(d => [d.step, d.amount])
    }))
});

const _updateSelectedSeries = (serie, selectedSeries) => {
    const selectedSpendingSeries = selectedSeries.slice();

    if (serie.visible) {
        selectedSpendingSeries.push(serie);
    } else {
        selectedSpendingSeries.splice(selectedSpendingSeries.indexOf(serie), ONE_ELEMENT);
    }

    return selectedSpendingSeries;

};

const CREATE_TOP_CONCEPT_DATA = 'CREATE_TOP_CONCEPT_DATA';

export const createTopConceptData = (concept, topByConcept, entities) => ({
    type: CREATE_TOP_CONCEPT_DATA,
    entities, topByConcept, concept
});

const _createTopConceptData = (topByConcept, entities = []) => entities.reduce((ret, item, index) => {
    const serie = topByConcept.find(_serie => _serie.id === item.id);

    if (serie) {
        ret.push({
            id:    index + ONE_ELEMENT,
            name:  item.name,
            value: serie.totalCost
        });
    }

    return ret;
}, []);

const CREATE_TOP_CONCEPT_CHART_CONFIG = 'CREATE_TOP_CONCEPT_CHART_CONFIG';

export const createTopConceptChartConfig = (concept, data, colors) => ({
    type: CREATE_TOP_CONCEPT_CHART_CONFIG,
    concept, data, colors
});

const _createTopConceptChartConfig = (data = [], colors) => ({
    ...getChartTop(),
    colors: colors || getColors(data.length),
    series: [{
        ...getChartTop().series[INIT_INDEX],
        data: data.map(d => [d.name, d.value])
    }]
});

const CREATE_SPENDING12_CONFIG = 'CREATE_SPENDING12_CONFIG';

export const createSpending12Config = (spendingLastYear, forecast, initChart) => ({
    type: CREATE_SPENDING12_CONFIG,
    spendingLastYear, forecast, initChart
});
const _createSpending12Config = (spendingLastYear, forecast, initChart) => {
    const
        categories = spendingLastYear.reduce((_categories, {data}) => data.reduce((_categoriesData, {month}) => {
            !~_categoriesData.indexOf(month) && _categoriesData.push(month);

            return _categoriesData.sort((a, b) => a > b ? SORT.SORT_ASC : SORT.SORT_DESC);
        }, _categories), []),
        getAmount = item => item && item.amount;

    let nextDate, next2Date;


    const series = spendingLastYear.map(({vendor: {code, title}, data}) => {
        const forecastVendor = forecast.find(item => item.vendor.code === code);
        const _data = categories.map(category => getAmount(data.find(({month}) => category === month)) || DEFAULT_VALUE);

        if (forecastVendor) {
            if (forecastVendor.next) {
                if (!nextDate) {
                    nextDate = new Date(categories.slice().pop());
                    nextDate.setMonth(nextDate.getMonth() + NEXT_MONTH);
                }

                _data.push(forecastVendor.next);
            }
            if (forecastVendor.next2) {
                if (!next2Date) {
                    next2Date = new Date(nextDate);
                    next2Date.setMonth(next2Date.getMonth() + NEXT_MONTH);
                }

                _data.push(forecastVendor.next2);
            }
        }

        return {
            name: title,
            data: _data
        };
    });

    let plotBands;

    if (nextDate) {
        categories.push(nextDate.getTime());

        plotBands = [{
            from:  categories.length - NEXT_MONTH - PLOT_ADJUST,
            to:    categories.length,
            color: '#ebefee'
        }];
    }
    if (next2Date) {
        categories.push(next2Date.getTime());

        plotBands = [{
            from:  categories.length - NEXT2_MONTH - PLOT_ADJUST,
            to:    categories.length,
            color: '#ebefee'
        }];
    }

    return {
        ...initChart,
        series,
        xAxis: {
            ...initChart.xAxis,
            categories,
            plotBands
        }
    };
};

const SET_SHOW_DETAILS = 'SET_SHOW_DETAILS';

export const setShowDetails = showDetails => ({
    type: SET_SHOW_DETAILS,
    showDetails
});

export const reducer = (state = _getDefaultState(), action = {}) => {
    switch (action.type) {
    case CHANGE_PERIOD:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                selectedSeries: defaultSelectedSeries
            }
        };
    case SELECT_SPENDING_CONCEPT:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                spendingConcept: action.concept,
                selectedSeries:  defaultSelectedSeries
            }
        };
    case CREATE_BREAKDOWN_CHART_CONFIG:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                selectedSeries: defaultSelectedSeries,
                chart:          {
                    ...state.breakdown.chart,
                    [state.breakdown.spendingConcept]: _createBreakdownChartConfig(action.data, action.colors, action.handleSelected, action.handleLoad, action.handleClick)
                }

            }
        };
    case CREATE_TOP_CONCEPT_DATA:
        return {
            ...state,
            topByConcept: {
                ...state.topByConcept,
                [action.concept]: {
                    ...state.topByConcept[action.concept],
                    data: _createTopConceptData(action.topByConcept, action.entities)
                }

            }
        };
    case CREATE_TOP_CONCEPT_CHART_CONFIG:
        return {
            ...state,
            topByConcept: {
                ...state.topByConcept,
                [action.concept]: {
                    ...state.topByConcept[action.concept],
                    chart: _createTopConceptChartConfig(action.data, action.colors)
                }

            }
        };
    case CREATE_SPENDING12_CONFIG:
        return {
            ...state,
            spendingLastYearConfig: _createSpending12Config(action.spendingLastYear, action.forecast, action.initChart)
        };
    case FILL_SELECTED_TOTAL_SPENDING_SERIES:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                selectedSeries: _fillSelectedSpendingSeries(action.selectedSeries)
            }

        };
    case TOGGLE_SELECTED_SERIES:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                selectedSeries: _updateSelectedSeries(action.serie, state.breakdown.selectedSeries)
            }
        };
    case SET_FETCHING_DASHBOARD:
        return _setFetchingDashboard(state, action);
    case GENERATE_FORECAST:
        return _generateForecast(state, action);
    case SET_SHOW_DETAILS:
        return {
            ...state,
            breakdown: {
                ...state.breakdown,
                showDetails: action.showDetails
            }
        };
    default:
        return state;
    }


};



// WEBPACK FOOTER //
// ./src/modules/dashboard.js